import { Badge } from '@thefarmersfront/kitchen';

import type { ColDef, GridOptions, ICellRendererParams, IRowNode, ValueFormatterParams } from 'ag-grid-community';

import { ItemListCell, TextButtonCell } from '~/components/common';

import { maskName } from '~/utils/common';
import { formatDate } from '~/utils/date';

import { DATE_FORMAT } from '~/constants';
import { CLAIM_RETURN_STATUS_BADGE_COLOR, type TypeClaimReturnStatus } from '~/constants/claim';

import type { TypeExchangeData } from '~/types/claim.types';

/**
 * [교환 신청/조회] 페이지 > 테이블 컬럼 정의
 */
const getClaimExchangeColumnDefs = ({
  onClickExchangeReceiptId,
  onClickExchangeOrderCode,
}: {
  onClickExchangeReceiptId: (exchangeReceiptId: string) => void;
  onClickExchangeOrderCode: (outboundOrderCode: string) => void;
}) => {
  const gridOptions: GridOptions = {
    rowSelection: 'single',
  };

  const columnDefs: ColDef<TypeExchangeData>[] = [
    {
      headerName: 'No',
      valueGetter: 'node.rowIndex + 1',
      width: 72,
      checkboxSelection: true,
    },
    {
      field: 'status',
      headerName: '교환상태',
      cellDataType: false,
      width: 100,
      cellRenderer: Badge,
      cellRendererParams: ({ value }: ICellRendererParams<TypeExchangeData, TypeExchangeData['status']>) => ({
        variant: 'light',
        status: true,
        color: CLAIM_RETURN_STATUS_BADGE_COLOR[value?.code as TypeClaimReturnStatus],
        children: value?.name,
      }),
      cellStyle: { display: 'flex', alignItems: 'center' },
    },
    {
      field: 'exchangeReceiptAt',
      headerName: '교환접수일시',
      width: 160,
      valueFormatter: ({ value }: ValueFormatterParams<TypeExchangeData, TypeExchangeData['exchangeReceiptAt']>) =>
        formatDate(value, DATE_FORMAT.DATE_TIME) ?? '-',
    },
    {
      field: 'clientOrderCode',
      headerName: '원판매처 주문번호',
      width: 180,
    },
    {
      field: 'exchangeReceiptId',
      headerName: '교환요청번호',
      width: 180,
      cellRenderer: TextButtonCell,
      cellRendererParams: ({ data }: ICellRendererParams<TypeExchangeData, TypeExchangeData['exchangeReceiptId']>) => ({
        children: data?.exchangeReceiptId,
        onClick: ({ data }: IRowNode<TypeExchangeData>) => {
          if (!data) return;
          onClickExchangeReceiptId(data.exchangeReceiptId);
        },
      }),
      cellStyle: { display: 'flex', alignItems: 'center' },
    },
    {
      field: 'member.name',
      headerName: '수령자명',
      width: 100,
      valueFormatter: ({ value }: ValueFormatterParams<TypeExchangeData, TypeExchangeData['member']['name']>) =>
        maskName(value) ?? '-',
    },
    {
      field: 'returnCourier.name',
      headerName: '회수 배송사',
      width: 88,
    },
    {
      field: 'returnOrderTrackings',
      headerName: '회수 송장번호',
      minWidth: 280,
      flex: 1,
      valueFormatter: ({ value }: ValueFormatterParams<TypeExchangeData, TypeExchangeData['returnOrderTrackings']>) => {
        if (!value || value.length === 0) return '-';
        return value.length > 1 ? `${value[0].invoiceNumber} 외 ${value.length - 1}개` : value[0].invoiceNumber;
      },
    },
    {
      field: 'outboundOrderCode',
      headerName: '원출고요청번호',
      width: 180,
    },
    {
      headerName: '회수 상품명',
      minWidth: 100,
      width: 240,
      cellRenderer: ItemListCell,
      cellRendererParams: ({ data }: ICellRendererParams<TypeExchangeData, TypeExchangeData['returnOrderItems']>) => ({
        value: data?.returnOrderItems ?? [],
        fieldName: 'goodsName',
      }),
      cellStyle: { overflow: 'hidden', whiteSpace: 'normal' },
    },
    {
      field: 'exchangeType.name',
      headerName: '선교환여부',
      width: 100,
    },
    {
      field: 'exchangeOutboundOrderCode',
      headerName: '교환배송 출고요청번호',
      width: 180,
      cellRenderer: TextButtonCell,
      cellRendererParams: ({
        data,
      }: ICellRendererParams<TypeExchangeData, TypeExchangeData['exchangeOutboundOrderCode']>) => ({
        children: data?.exchangeOutboundOrderCode,
        onClick: ({ data }: IRowNode<TypeExchangeData>) => {
          if (!data) return;
          onClickExchangeOrderCode(data.exchangeOutboundOrderCode);
        },
      }),
      cellStyle: { display: 'flex', alignItems: 'center' },
    },
    {
      headerName: '교환 배송 상품명',
      minWidth: 100,
      width: 240,
      cellRenderer: ItemListCell,
      cellRendererParams: ({
        data,
      }: ICellRendererParams<TypeExchangeData, TypeExchangeData['exchangeOrderItems']>) => ({
        value: data?.exchangeOrderItems ?? [],
        fieldName: 'goodsName',
      }),
      cellStyle: { overflow: 'hidden', whiteSpace: 'normal' },
    },
    {
      field: 'updatedAt',
      headerName: '최종 업데이트일시',
      width: 159,
      valueFormatter: ({ value }: ValueFormatterParams<TypeExchangeData, TypeExchangeData['updatedAt']>) =>
        formatDate(value, DATE_FORMAT.DATE_TIME) ?? '-',
    },
  ];

  return { gridOptions, columnDefs };
};

export default getClaimExchangeColumnDefs;
